import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CheckCircle2 } from 'lucide-react';
import api from '../services/api';
import { Card, Button } from './index';
import { ScanVragenlijst, ScanVraag, ScanAntwoorden, scanCompleet } from './ScanVragenlijst';

interface Peiling {
  id: string;
  titel: string;
  eind_op: string;
  vragen: ScanVraag[];
}

const getPeilingen = async () => (await api.get<{ data: Peiling[] }>('/medewerker/peilingen')).data.data;

const PeilingFormulier: React.FC<{ p: Peiling; onKlaar: (titel: string) => void }> = ({ p, onKlaar }) => {
  const { t } = useTranslation('medewerker');
  const qc = useQueryClient();
  const [antwoorden, setAntwoorden] = useState<ScanAntwoorden>({});
  const mut = useMutation({
    mutationFn: () => api.post(`/medewerker/peilingen/${p.id}`, { antwoorden }),
    onSuccess: () => {
      // De token is vernietigd: de peiling verdwijnt uit de lijst
      onKlaar(p.titel);
      qc.invalidateQueries({ queryKey: ['medewerker-peilingen'] });
    },
  });
  const compleet = scanCompleet(p.vragen, antwoorden);
  const fout = (mut.error as any)?.response?.data?.message;

  return (
    <Card className="p-5">
      <h2 className="text-lg font-semibold text-gray-900">{p.titel}</h2>
      <p className="text-xs text-gray-500">
        {t('peiling.openTot', { defaultValue: 'Open tot {{datum}}', datum: new Date(p.eind_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' }) })}
      </p>
      <form
        className="mt-4 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (compleet) mut.mutate();
        }}
      >
        <ScanVragenlijst vragen={p.vragen} antwoorden={antwoorden} zet={(id, w) => setAntwoorden((a) => ({ ...a, [id]: w }))} />
        {fout && <p className="text-sm text-red-600" role="alert">{fout}</p>}
        <Button type="submit" className="w-full" disabled={!compleet || mut.isPending} loading={mut.isPending}>
          {t('thermometer.versturen', { defaultValue: 'Anoniem versturen' })}
        </Button>
      </form>
    </Card>
  );
};

/** Lopende pulse-peilingen van de werkgever (Menu 4 DATA → Bedrijfsthermometer & Pulse Regie). */
export const MedewerkerPeilingen: React.FC = () => {
  const { t } = useTranslation('medewerker');
  const q = useQuery({ queryKey: ['medewerker-peilingen'], queryFn: getPeilingen });
  const [klaar, setKlaar] = useState<string[]>([]);
  const peilingen = q.data ?? [];
  if (!peilingen.length && !klaar.length) return null;
  return (
    <section className="space-y-4" aria-labelledby="peilingen-kop">
      <h2 id="peilingen-kop" className="text-xl font-bold text-gray-900">{t('peiling.titel', { defaultValue: 'Lopende peilingen' })}</h2>
      {klaar.map((titel) => (
        <Card key={titel} className="flex items-center gap-3 p-4" role="status">
          <CheckCircle2 className="h-6 w-6 flex-shrink-0 text-green-600" aria-hidden />
          <p className="text-sm text-gray-800">{t('peiling.ingevuld', { defaultValue: 'Je hebt "{{titel}}" ingevuld. Dank je wel!', titel })}</p>
        </Card>
      ))}
      {peilingen.map((p) => <PeilingFormulier key={p.id} p={p} onKlaar={(titel) => setKlaar((k) => [...k, titel])} />)}
    </section>
  );
};

export default MedewerkerPeilingen;
